import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, Building, Search, MapPin, Users, CheckCircle2,
  XCircle, Clock, Eye, Loader2, Trash2, Pencil, Save, Plus,
  Sparkles, ImageIcon, Wand2, ExternalLink, X, Upload, FileText, Download,
  Bot,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminVenues() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [venues, setVenues] = useState([]);
  const [counts, setCounts] = useState({ total: 0, active: 0, pending: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editVenue, setEditVenue] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Google Places import flow — admin types a name, picks a suggestion,
  // we backend-fetch the place_id and create a fully-enriched venue
  // (real photos uploaded to our CDN + AI editorial description).
  const [importOpen, setImportOpen] = useState(false);
  const [importTab, setImportTab] = useState('eventismbot'); // 'eventismbot' | 'google'
  const [importQuery, setImportQuery] = useState('');
  const [importSuggestions, setImportSuggestions] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const importDebounce = useRef(null);

  // EventismBot "Add Venue by URL" — free scrape + preview + confirm.
  const [botUrl, setBotUrl] = useState('');
  const [botScraping, setBotScraping] = useState(false);
  const [botPreview, setBotPreview] = useState(null); // { extracted: {...} }
  const [botSaving, setBotSaving] = useState(false);

  // Manual create flow — the escape hatch when Places is unavailable
  // (offline, quota exhausted, long-tail venue not on Google yet).
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    name: '', city: 'Denver', state: 'CO', address: '', zip_code: '',
    venue_type: 'event_space', neighborhood: '', description: '',
    phone: '', website: '', capacity_min: '', capacity_max: '',
    price_band: '', photos: [], amenities_csv: '', tags_csv: '',
    auto_enrich: true,
  });
  const [manualSaving, setManualSaving] = useState(false);
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  // Bulk import — two flavors in one modal:
  //   • URL list mode: paste one URL per line, we scrape + create for each
  //   • CSV mode: paste CSV text with headers, we create a row per venue
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState('urls'); // 'urls' | 'csv'
  const [bulkUrls, setBulkUrls] = useState('');
  const [bulkCsv, setBulkCsv] = useState('');
  const [bulkCityDefault, setBulkCityDefault] = useState('Denver');
  const [bulkStateDefault, setBulkStateDefault] = useState('CO');
  const [bulkAutoEnrich, setBulkAutoEnrich] = useState(true);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const bulkCsvFileRef = useRef(null);

  // Per-row "Verify via Google Places" action + the bulk "Verify & Enrich
  // missing data" sweep. Keyed by venue_id so multiple verifies can
  // surface independent spinners.
  const [verifyingId, setVerifyingId] = useState(null);
  const [bulkVerifying, setBulkVerifying] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchVenues(); }, []);

  const fetchVenues = async () => {
    try {
      const res = await axios.get(`${API}/admin/venues/all`, { headers });
      setVenues(res.data.venues || []);
      setCounts(res.data.counts || {});
    } catch { toast.error('Failed to load venues'); }
    finally { setLoading(false); }
  };

  const approveVenue = async (venueId) => {
    try {
      await axios.put(`${API}/admin/venues/${venueId}/approve`, {}, { headers });
      toast.success('Venue approved!');
      fetchVenues();
    } catch { toast.error('Failed to approve'); }
  };

  const rejectVenue = async (venueId) => {
    const reason = window.prompt('Rejection reason (optional):');
    if (reason === null) return;
    try {
      await axios.put(`${API}/admin/venues/${venueId}/reject`, { reason }, { headers });
      toast.success('Venue rejected');
      fetchVenues();
    } catch { toast.error('Failed to reject'); }
  };

  const deleteVenue = async (venueId) => {
    try {
      await axios.delete(`${API}/admin/venues/${venueId}`, { headers });
      toast.success('Venue deleted');
      setDeleteConfirm(null);
      fetchVenues();
    } catch { toast.error('Failed to delete venue'); }
  };

  const openEdit = (venue) => {
    setEditVenue(venue);
    setEditForm({
      name: venue.name || '',
      description: venue.description || '',
      status: venue.status || 'active',
      phone: venue.phone || '',
      email: venue.email || '',
      website: venue.website || '',
      max_capacity: venue.max_capacity || '',
      venue_type: venue.venue_type || '',
      city: venue.address?.city || '',
      state: venue.address?.state || '',
      street: venue.address?.street || '',
      zip: venue.address?.zip || '',
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: editForm.name,
        description: editForm.description,
        status: editForm.status,
        phone: editForm.phone,
        email: editForm.email,
        website: editForm.website,
        max_capacity: parseInt(editForm.max_capacity) || null,
        venue_type: editForm.venue_type,
        address: { street: editForm.street, city: editForm.city, state: editForm.state, zip: editForm.zip },
      };
      await axios.put(`${API}/admin/venues/${editVenue.venue_id}/edit`, payload, { headers });
      toast.success('Venue updated');
      setEditVenue(null);
      fetchVenues();
    } catch { toast.error('Failed to update venue'); }
    finally { setSaving(false); }
  };

  // ----- Google Places import: live-search → pick → backend creates a
  // fully-enriched venue (photos uploaded to our CDN, AI description). -----
  useEffect(() => {
    if (importDebounce.current) clearTimeout(importDebounce.current);
    if (importQuery.trim().length < 2) {
      setImportSuggestions([]);
      return;
    }
    importDebounce.current = setTimeout(async () => {
      setImportLoading(true);
      try {
        const res = await axios.get(`${API}/places/autocomplete`, {
          params: { q: importQuery }, headers,
        });
        setImportSuggestions(res.data?.results || []);
      } catch {
        setImportSuggestions([]);
      } finally {
        setImportLoading(false);
      }
    }, 280);
    return () => importDebounce.current && clearTimeout(importDebounce.current);
  }, [importQuery]);

  const importFromPlaces = async (place) => {
    if (!place?.place_id) return;
    setImporting(true);
    const t = toast.loading(`Importing ${place.name}…`);
    try {
      const res = await axios.post(
        `${API}/admin/venues/import-from-places`,
        {
          place_id: place.place_id,
          city: place.city || undefined,
          state: place.state || undefined,
        },
        { headers },
      );
      const created = res.data?.created;
      const venue = res.data?.venue;
      const photoCount = (venue?.photos || []).length;
      toast.success(
        created
          ? `Created "${venue?.name}" — ${photoCount} photo${photoCount === 1 ? '' : 's'} imported`
          : `Re-enriched "${venue?.name}" — ${photoCount} photo${photoCount === 1 ? '' : 's'} now stored`,
        { id: t },
      );
      setImportOpen(false);
      setImportQuery('');
      setImportSuggestions([]);
      fetchVenues();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed', { id: t });
    } finally {
      setImporting(false);
    }
  };

  // ----- EventismBot "Add Venue by URL" — free preview + confirm -----
  const scrapeForBotPreview = async () => {
    const url = (botUrl || '').trim();
    if (!url) return toast.error('Paste the venue’s website URL first');
    setBotScraping(true);
    setBotPreview(null);
    const t = toast.loading('EventismBot reading the site…');
    try {
      const res = await axios.post(
        `${API}/admin/venues/scrape-url`,
        { url, mirror_images: false },
        { headers, timeout: 45000 },
      );
      const ex = res.data?.extracted || {};
      const gotCount = [ex.name, ex.description, ex.address, ex.phone, (ex.photos || []).length]
        .filter(Boolean).length;
      if (!ex.name) {
        toast.warning('No structured venue data on that page. Try their homepage or the /about URL.', { id: t });
      } else {
        toast.success(`Scraped ${gotCount} field${gotCount === 1 ? '' : 's'}. Review below and save.`, { id: t });
      }
      setBotPreview({ url, extracted: ex });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Couldn’t read that page', { id: t });
    } finally {
      setBotScraping(false);
    }
  };

  const updateBotPreviewField = (key, value) => {
    setBotPreview((p) => (p ? { ...p, extracted: { ...p.extracted, [key]: value } } : p));
  };
  const removeBotPreviewPhoto = (idx) => {
    setBotPreview((p) => {
      if (!p) return p;
      const photos = (p.extracted.photos || []).filter((_, i) => i !== idx);
      return { ...p, extracted: { ...p.extracted, photos } };
    });
  };

  const saveBotPreview = async () => {
    if (!botPreview) return;
    const ex = botPreview.extracted || {};
    const name = (ex.name || '').trim();
    const city = (ex.city || 'Denver').trim();
    if (!name || name.length < 2) return toast.error('Venue name is required');
    if (!city) return toast.error('City is required');
    setBotSaving(true);
    const t = toast.loading(`Creating ${name}…`);
    try {
      const payload = {
        name,
        city,
        state: ex.state || 'CO',
        address: ex.address || '',
        zip_code: ex.zip_code || '',
        venue_type: ex.venue_type || 'event_space',
        neighborhood: ex.neighborhood || '',
        description: ex.description || '',
        phone: ex.phone || undefined,
        website: ex.website || botPreview.url,
        photos: ex.photos || [],
        auto_enrich: true,
      };
      const res = await axios.post(`${API}/admin/venues/create-manual`, payload, { headers });
      toast.success(`Created "${res.data?.venue?.name || name}"`, { id: t });
      setImportOpen(false);
      setBotUrl('');
      setBotPreview(null);
      fetchVenues();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed', { id: t });
    } finally {
      setBotSaving(false);
    }
  };

  // ----- Manual venue create -----
  const csv = (s) => (s || '').split(',').map((v) => v.trim()).filter(Boolean);

  const scrapeVenueFromUrl = async () => {
    const url = (scrapeUrl || '').trim();
    if (!url) return toast.error('Paste the venue’s website URL first');
    setScraping(true);
    const t = toast.loading('Reading the venue site…');
    try {
      const res = await axios.post(
        `${API}/admin/venues/scrape-url`,
        { url },
        { headers },
      );
      const ex = res.data?.extracted || {};
      // Merge — only overwrite empty fields so the admin's typed data wins.
      setManualForm((f) => ({
        ...f,
        name: f.name || ex.name || '',
        description: f.description || ex.description || '',
        address: f.address || ex.address || '',
        city: f.city || ex.city || 'Denver',
        state: f.state || ex.state || 'CO',
        zip_code: f.zip_code || ex.zip_code || '',
        phone: f.phone || ex.phone || '',
        website: f.website || ex.website || url,
        venue_type: (f.venue_type && f.venue_type !== 'event_space') ? f.venue_type : (ex.venue_type || 'event_space'),
        photos: [...(f.photos || []), ...(ex.photos || [])].filter((v, i, a) => v && a.indexOf(v) === i).slice(0, 8),
      }));
      const gotCount = [ex.name, ex.description, ex.address, ex.phone, ex.photos?.length].filter(Boolean).length;
      toast.success(`Prefilled ${gotCount} field${gotCount === 1 ? '' : 's'} from the site. Review & edit before saving.`, { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Couldn’t read that page', { id: t });
    } finally {
      setScraping(false);
    }
  };

  const uploadVenuePhoto = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => (f.type || '').startsWith('image/'));
    if (!files.length) return;
    setUploadingPhoto(true);
    const t = toast.loading(`Uploading ${files.length} photo${files.length === 1 ? '' : 's'}…`);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/admin/venues/upload-photo`, fd, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        if (res.data?.url) uploaded.push(res.data.url);
      }
      setManualForm((f) => ({ ...f, photos: [...(f.photos || []), ...uploaded] }));
      toast.success(`Uploaded ${uploaded.length} photo${uploaded.length === 1 ? '' : 's'}`, { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed', { id: t });
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const removeVenuePhoto = (idx) => {
    setManualForm((f) => ({ ...f, photos: (f.photos || []).filter((_, i) => i !== idx) }));
  };

  const saveManualVenue = async () => {
    const name = (manualForm.name || '').trim();
    const city = (manualForm.city || '').trim();
    if (!name || name.length < 2) return toast.error('Venue name is required');
    if (!city) return toast.error('City is required');
    setManualSaving(true);
    const t = toast.loading(`Creating ${name}…`);
    try {
      const payload = {
        name,
        city,
        state: manualForm.state || undefined,
        address: manualForm.address || '',
        zip_code: manualForm.zip_code || '',
        venue_type: manualForm.venue_type || 'event_space',
        neighborhood: manualForm.neighborhood || '',
        description: manualForm.description || '',
        phone: manualForm.phone || undefined,
        website: manualForm.website || undefined,
        capacity_min: manualForm.capacity_min ? parseInt(manualForm.capacity_min, 10) : undefined,
        capacity_max: manualForm.capacity_max ? parseInt(manualForm.capacity_max, 10) : undefined,
        price_band: manualForm.price_band || undefined,
        photos: manualForm.photos || [],
        amenities: csv(manualForm.amenities_csv),
        tags: csv(manualForm.tags_csv),
        auto_enrich: !!manualForm.auto_enrich,
      };
      const res = await axios.post(`${API}/admin/venues/create-manual`, payload, { headers });
      toast.success(`Created "${res.data?.venue?.name || name}"`, { id: t });
      setManualOpen(false);
      setManualForm((f) => ({
        ...f, name: '', address: '', zip_code: '', neighborhood: '', description: '',
        phone: '', website: '', capacity_min: '', capacity_max: '', price_band: '',
        photos: [], amenities_csv: '', tags_csv: '',
      }));
      setScrapeUrl('');
      fetchVenues();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed', { id: t });
    } finally {
      setManualSaving(false);
    }
  };

  // ----- Bulk import (URL list or CSV) -----
  const runBulkImport = async () => {
    setBulkRunning(true);
    setBulkResult(null);
    const t = toast.loading('Importing venues…');
    try {
      let res;
      if (bulkMode === 'urls') {
        const urls = (bulkUrls || '').split('\n').map((s) => s.trim()).filter(Boolean);
        if (!urls.length) { toast.error('Paste at least one URL', { id: t }); setBulkRunning(false); return; }
        if (urls.length > 50) { toast.error('Max 50 URLs per batch', { id: t }); setBulkRunning(false); return; }
        res = await axios.post(`${API}/admin/venues/bulk-scrape`, {
          urls,
          city_default: bulkCityDefault || 'Denver',
          state_default: bulkStateDefault || null,
          auto_enrich: bulkAutoEnrich,
        }, { headers });
      } else {
        if (!(bulkCsv || '').trim()) { toast.error('Paste CSV text or upload a .csv file', { id: t }); setBulkRunning(false); return; }
        res = await axios.post(`${API}/admin/venues/bulk-csv`, {
          csv_text: bulkCsv,
          city_default: bulkCityDefault || 'Denver',
          state_default: bulkStateDefault || null,
          auto_enrich: bulkAutoEnrich,
        }, { headers });
      }
      setBulkResult(res.data);
      const s = res.data?.summary || {};
      toast.success(`Imported ${s.created || 0} venue${s.created === 1 ? '' : 's'}${s.skipped ? ` · ${s.skipped} skipped` : ''}${s.errors ? ` · ${s.errors} error${s.errors === 1 ? '' : 's'}` : ''}`, { id: t });
      fetchVenues();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk import failed', { id: t });
    } finally {
      setBulkRunning(false);
    }
  };

  const onBulkCsvFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    setBulkCsv(text);
    toast.success(`Loaded ${file.name} (${text.split('\n').length - 1} rows)`);
  };

  const downloadCsvTemplate = () => {
    const cols = 'name,city,state,address,zip_code,venue_type,neighborhood,description,phone,website,capacity_min,capacity_max,price_band,tags,amenities,lat,lng,photo1,photo2,photo3,photo4,photo5';
    const example = 'Great Divide Brewing Co.,Denver,CO,2201 Arapahoe St,80205,brewery,Ballpark,"An OG Denver craft brewery with a heated patio.",303-296-9460,https://greatdivide.com,10,150,$$,"brewery,craft beer,patio","Outdoor Seating,Dog Friendly",39.7539,-105.0002,https://picsum.photos/800/500?a,https://picsum.photos/800/500?b,,,';
    const blob = new Blob([cols + '\n' + example + '\n'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'eventism-venues-template.csv';
    a.click(); URL.revokeObjectURL(url);
  };

  // ----- Per-row "Verify via Google Places" -----
  const [rehydrating, setRehydrating] = useState(null);
  const rehydrateVenuePhotos = async (venue) => {
    // EventismBot one-click: scrape the venue's own site (or ask the
    // admin for a URL) and mirror all discovered photos to our CDN.
    // Zero Google Places calls — free forever.
    let url = venue.website || '';
    if (!url) {
      url = window.prompt(
        `We don't have a website for "${venue.name}". Paste the venue's website URL to scrape:`,
        '',
      );
      if (!url) return;
    }
    setRehydrating(venue.venue_id);
    const t = toast.loading(`EventismBot reading ${venue.name}…`);
    try {
      const res = await axios.post(
        `${API}/admin/venues/${venue.venue_id}/rehydrate-photos`,
        { url },
        { headers, timeout: 60000 },
      );
      const n = res.data?.added || 0;
      if (n === 0) {
        toast.warning(res.data?.message || 'No new photos found', { id: t });
      } else {
        toast.success(`EventismBot mirrored ${n} venue photo${n === 1 ? '' : 's'} to our CDN`, { id: t });
        fetchVenues();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rehydrate failed', { id: t });
    } finally {
      setRehydrating(null);
    }
  };

  const verifyOne = async (venueId, venueName) => {
    setVerifyingId(venueId);
    const t = toast.loading(`Verifying ${venueName}…`);
    try {
      const res = await axios.post(
        `${API}/admin/venues/${venueId}/verify-places`, {}, { headers },
      );
      const status = res.data?.status;
      if (status === 'verified') {
        const parts = [];
        parts.push(`${res.data?.photos_added || 0} photo(s) added`);
        if (res.data?.description_added) parts.push('description filled');
        if (res.data?.promoted_to_active) parts.push('promoted to Active');
        toast.success(`Verified — ${parts.join(', ')}`, { id: t });
        fetchVenues();
      } else if (status === 'no_match') {
        toast.warning(`No confident Google match for ${venueName}`, { id: t });
      } else if (status === 'quota_exceeded') {
        toast.error('Google Places daily quota exceeded — try tomorrow', { id: t });
      } else {
        toast.error(res.data?.reason || 'Verification failed', { id: t });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed', { id: t });
    } finally {
      setVerifyingId(null);
    }
  };

  // ----- Bulk "Verify & Enrich Missing" — EventismBot sweep -----
  // FREE for venues with a website. For venues without one, does a
  // single lightweight Google Places lookup ONLY to discover the site
  // URL, then scrapes + mirrors to our CDN. Anything Google can't
  // identify is bucketed into a `needs_website` toast so the admin
  // knows exactly which rows to fill in manually.
  const [needsWebsiteList, setNeedsWebsiteList] = useState([]);
  const bulkVerifyMissing = async () => {
    setBulkVerifying(true);
    setNeedsWebsiteList([]);
    const t = toast.loading('EventismBot sweeping the most incomplete venue profiles…');
    try {
      const res = await axios.post(
        `${API}/admin/bot/enrich-missing`,
        { limit: 20 },
        { headers, timeout: 60000 },
      );
      const started = res.data?.started || 0;
      if (started === 0) {
        toast.success('Nothing to do — every venue profile is already perfect.', { id: t });
      } else {
        toast.success(
          `EventismBot sweep started on ${started} profiles — it runs in the background. Track progress in the EventismBot dashboard.`,
          { id: t, duration: 8000 },
        );
      }
      fetchVenues();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk verify failed', { id: t });
    } finally {
      setBulkVerifying(false);
    }
  };

  const filtered = venues.filter(v => {
    if (filter !== 'all' && v.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(v.name?.toLowerCase().includes(q) || v.address?.city?.toLowerCase().includes(q) || v.owner_name?.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  if (user?.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="back-to-admin">
              <ArrowLeft className="h-4 w-4 mr-1" /> Admin
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground flex items-center gap-2">
                <Building className="h-6 w-6 text-orange-500" /> Venue Manager
              </h1>
              <p className="text-sm text-muted-foreground">{counts.total} venues total · powered by <span className="text-fuchsia-400 font-semibold">EventismBot</span></p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              className="h-9 text-xs border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
              onClick={() => navigate('/admin/eventism-bot')}
              data-testid="open-eventism-bot"
              title="EventismBot autopilot — per-city venue discovery, enrichment, and refresh"
            >
              <Bot className="h-3.5 w-3.5 mr-1.5" /> EventismBot
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-9 text-xs border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              onClick={bulkVerifyMissing}
              disabled={bulkVerifying}
              data-testid="bulk-verify-missing"
              title="Full EventismBot waterfall — Google Business data (DataForSEO) + website photos to CDN + AI descriptions + socials on the most incomplete profiles."
            >
              {bulkVerifying
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <Wand2 className="h-3.5 w-3.5 mr-1.5" />}
              Verify &amp; Enrich Missing
            </Button>
            <Button
              size="sm"
              className="h-9 text-xs bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white border-0 font-bold"
              onClick={() => { setImportOpen(true); setImportTab('eventismbot'); }}
              data-testid="add-venue-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Venue
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-9 text-xs border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              onClick={() => setManualOpen(true)}
              data-testid="add-venue-manually"
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Add Manually
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-9 text-xs border-fuchsia-500/40 text-fuchsia-200 hover:bg-fuchsia-500/10"
              onClick={() => { setBulkOpen(true); setBulkResult(null); }}
              data-testid="bulk-import-btn"
            >
              <Upload className="h-3.5 w-3.5 mr-1.5" /> Bulk Import
            </Button>
          </div>
        </div>

        {/* "Needs Website URL" banner from the last EventismBot sweep */}
        {needsWebsiteList.length > 0 && (
          <Card
            className="bg-amber-500/10 border-amber-500/40 mb-6"
            data-testid="needs-website-banner"
          >
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start gap-2">
                <Wand2 className="h-4 w-4 text-amber-400 mt-0.5" />
                <div className="flex-1 text-sm">
                  <p className="text-amber-200 font-semibold">
                    {needsWebsiteList.length} venue{needsWebsiteList.length === 1 ? '' : 's'} need a website URL
                  </p>
                  <p className="text-xs text-muted-foreground">
                    EventismBot couldn’t identify these on Google either. Click one to edit, paste its website, then re-run <b>Verify &amp; Enrich Missing</b>.
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-amber-300"
                  onClick={() => setNeedsWebsiteList([])}
                  data-testid="needs-website-dismiss"
                >
                  Dismiss
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {needsWebsiteList.map((n) => {
                  const v = venues.find((x) => x.venue_id === n.venue_id);
                  return (
                    <button
                      key={n.venue_id}
                      type="button"
                      onClick={() => v && openEdit(v)}
                      className="text-[11px] px-2 py-1 rounded-full bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-100 transition-colors"
                      data-testid={`needs-website-${n.venue_id}`}
                      title={`Open ${n.name} to add website`}
                    >
                      {n.name}{n.city ? ` · ${n.city}` : ''}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total', value: counts.total, color: 'text-foreground', f: 'all' },
            { label: 'Active', value: counts.active, color: 'text-emerald-500', f: 'active' },
            { label: 'Pending', value: counts.pending, color: 'text-amber-500', f: 'pending_review' },
            { label: 'Rejected', value: counts.rejected, color: 'text-red-500', f: 'rejected' },
          ].map(s => (
            <Card
              key={s.label}
              className={`bg-card border-border cursor-pointer transition-all ${filter === s.f ? 'ring-2 ring-indigo-500' : 'hover:border-indigo-500/30'}`}
              onClick={() => setFilter(s.f)}
              data-testid={`filter-${s.f}`}
            >
              <CardContent className="p-4 text-center">
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search venues by name, city, or owner..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-card border-border h-10" data-testid="venue-search" />
        </div>

        {/* Venue List */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>
        ) : filtered.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-semibold text-foreground mb-1">No venues found</p>
              <p className="text-sm text-muted-foreground">{filter !== 'all' ? 'Try a different filter' : 'Venue partners will appear here when they register'}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map(venue => (
              <Card key={venue.venue_id} className="bg-card border-border hover:border-indigo-500/30 transition-all" data-testid={`venue-${venue.venue_id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    {venue.images?.[0] || venue.photos?.[0] ? (
                      <img src={venue.images?.[0] || venue.photos?.[0]} alt={venue.name} className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div
                        className="w-20 h-20 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0"
                        title="No photo on file"
                        data-testid={`venue-no-photo-${venue.venue_id}`}
                      >
                        <ImageIcon className="h-7 w-7 text-amber-400/70" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-foreground truncate">{venue.name}</h3>
                        <Badge className={
                          venue.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                          venue.status === 'pending_review' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-red-500/20 text-red-400'
                        }>
                          {venue.status === 'pending_review' ? 'Pending' : venue.status === 'active' ? 'Active' : 'Rejected'}
                        </Badge>
                        {venue.verified && (
                          <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 text-[10px] uppercase tracking-widest">
                            <CheckCircle2 className="h-2.5 w-2.5 mr-1" /> Verified
                          </Badge>
                        )}
                        {!(venue.photos?.length) && (
                          <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px] uppercase tracking-widest">
                            Needs photos
                          </Badge>
                        )}
                        {!venue.description && (
                          <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px] uppercase tracking-widest">
                            Needs description
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        {venue.address && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {venue.address.city}, {venue.address.state}</span>}
                        {venue.max_capacity && <span className="flex items-center gap-1"><Users className="h-3 w-3" /> Up to {venue.max_capacity}</span>}
                        {venue.owner_name && <span>Owner: {venue.owner_name}</span>}
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(venue.created_at).toLocaleDateString()}</span>
                      </div>
                      {venue.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{venue.description}</p>}
                    </div>
                    <div className="flex gap-2 flex-shrink-0 flex-wrap">
                      {venue.status === 'pending_review' && (
                        <>
                          <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => approveVenue(venue.venue_id)} data-testid={`approve-${venue.venue_id}`}>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 text-xs text-red-400 border-red-500/30" onClick={() => rejectVenue(venue.venue_id)} data-testid={`reject-${venue.venue_id}`}>
                            <XCircle className="h-3 w-3 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      {(!(venue.photos?.length) || !venue.description || !venue.verified) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs text-amber-300 border-amber-500/40 hover:bg-amber-500/10"
                          onClick={() => verifyOne(venue.venue_id, venue.name)}
                          disabled={verifyingId === venue.venue_id}
                          data-testid={`verify-${venue.venue_id}`}
                        >
                          {verifyingId === venue.venue_id
                            ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            : <Sparkles className="h-3 w-3 mr-1" />}
                          {venue.verified ? 'Re-enrich' : 'Verify'}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-xs text-fuchsia-300 border-fuchsia-500/40 hover:bg-fuchsia-500/10"
                        onClick={() => rehydrateVenuePhotos(venue)}
                        disabled={rehydrating === venue.venue_id}
                        data-testid={`rehydrate-${venue.venue_id}`}
                        title="EventismBot — free scraper: reads the venue's own website, filters logos/icons, and mirrors all real photos to our CDN"
                      >
                        {rehydrating === venue.venue_id
                          ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          : <Wand2 className="h-3 w-3 mr-1" />}
                        Rehydrate
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => openEdit(venue)} data-testid={`edit-${venue.venue_id}`}>
                        <Pencil className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      {venue.slug && (
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => window.open(`/venues/colorado/denver/all/${venue.slug}`, '_blank')} data-testid={`view-${venue.venue_id}`}>
                          <Eye className="h-3 w-3 mr-1" /> View
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-8 text-xs text-red-400 border-red-500/30 hover:bg-red-500/10" onClick={() => setDeleteConfirm(venue)} data-testid={`delete-${venue.venue_id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Venue — EventismBot default (free) + Google Places fallback */}
      <Dialog
        open={importOpen}
        onOpenChange={(open) => {
          if (!open) {
            setImportOpen(false);
            setImportQuery('');
            setImportSuggestions([]);
            setBotUrl('');
            setBotPreview(null);
          }
        }}
      >
        <DialogContent
          className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="add-venue-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Plus className="h-5 w-5 text-fuchsia-400" /> Add Venue
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              EventismBot scrapes venue sites for free and mirrors photos to our CDN. Google Places is a paid fallback for venues without a website.
            </p>
          </DialogHeader>

          <Tabs value={importTab} onValueChange={setImportTab} className="w-full">
            <TabsList
              className="grid grid-cols-2 w-full bg-muted/40"
              data-testid="add-venue-tabs"
            >
              <TabsTrigger
                value="eventismbot"
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-fuchsia-500/20 data-[state=active]:to-indigo-500/20"
                data-testid="tab-eventismbot"
              >
                <Bot className="h-3.5 w-3.5 mr-1.5 text-fuchsia-400" />
                EventismBot <span className="ml-1 text-[9px] text-emerald-300 font-bold">FREE</span>
              </TabsTrigger>
              <TabsTrigger
                value="google"
                className="data-[state=active]:bg-muted"
                data-testid="tab-google-places"
              >
                <Search className="h-3.5 w-3.5 mr-1.5 text-amber-400" />
                Google Places
                <span className="ml-1 text-[9px] text-amber-300 font-bold">FALLBACK</span>
              </TabsTrigger>
            </TabsList>

            {/* --- EventismBot tab (default) --- */}
            <TabsContent value="eventismbot" className="space-y-3 pt-3">
              <div className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-3 space-y-2">
                <Label className="text-fuchsia-200 text-xs font-semibold flex items-center gap-2">
                  <Bot className="h-3.5 w-3.5" /> Paste the venue’s website
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={botUrl}
                    onChange={(e) => setBotUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') scrapeForBotPreview(); }}
                    placeholder="https://greatdivide.com"
                    className="flex-1"
                    data-testid="bot-url-input"
                    autoFocus
                  />
                  <Button
                    onClick={scrapeForBotPreview}
                    disabled={botScraping || !botUrl.trim()}
                    variant="outline"
                    className="border-fuchsia-500/50 text-fuchsia-200 hover:bg-fuchsia-500/10 shrink-0"
                    data-testid="bot-scrape-btn"
                  >
                    {botScraping
                      ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Reading…</>
                      : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Scrape</>}
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  EventismBot reads JSON-LD + Open Graph tags — the same signals Google reads. Free, no API keys, no rate limits. Photos get mirrored to our CDN on save.
                </p>
              </div>

              {botPreview && (
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-3" data-testid="bot-preview">
                  <div className="flex items-center gap-2 text-emerald-300 text-xs font-semibold">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Preview — edit anything below, then save
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <div className="sm:col-span-2 space-y-1">
                      <Label className="text-[11px]">Name *</Label>
                      <Input
                        value={botPreview.extracted.name || ''}
                        onChange={(e) => updateBotPreviewField('name', e.target.value)}
                        data-testid="bot-preview-name"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px]">City</Label>
                      <Input
                        value={botPreview.extracted.city || ''}
                        onChange={(e) => updateBotPreviewField('city', e.target.value)}
                        data-testid="bot-preview-city"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px]">State</Label>
                      <Input
                        value={botPreview.extracted.state || ''}
                        onChange={(e) => updateBotPreviewField('state', e.target.value)}
                        data-testid="bot-preview-state"
                      />
                    </div>
                    <div className="sm:col-span-2 space-y-1">
                      <Label className="text-[11px]">Address</Label>
                      <Input
                        value={botPreview.extracted.address || ''}
                        onChange={(e) => updateBotPreviewField('address', e.target.value)}
                        data-testid="bot-preview-address"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px]">Phone</Label>
                      <Input
                        value={botPreview.extracted.phone || ''}
                        onChange={(e) => updateBotPreviewField('phone', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px]">Type</Label>
                      <select
                        value={botPreview.extracted.venue_type || 'event_space'}
                        onChange={(e) => updateBotPreviewField('venue_type', e.target.value)}
                        className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                      >
                        {['event_space','brewery','winery','wine_bar','cocktail_bar','bar','restaurant','cafe','nightclub','hotel','venue'].map((t) => (
                          <option key={t} value={t}>{t.replace(/_/g,' ')}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2 space-y-1">
                      <Label className="text-[11px]">Description</Label>
                      <Textarea
                        rows={3}
                        value={botPreview.extracted.description || ''}
                        onChange={(e) => updateBotPreviewField('description', e.target.value)}
                        data-testid="bot-preview-description"
                      />
                    </div>
                  </div>

                  {(botPreview.extracted.photos || []).length > 0 && (
                    <div className="space-y-1">
                      <Label className="text-[11px]">Photos (mirrored to our CDN on save)</Label>
                      <div className="grid grid-cols-4 gap-2">
                        {(botPreview.extracted.photos || []).slice(0, 8).map((p, i) => (
                          <div key={p + i} className="relative aspect-video rounded overflow-hidden border border-border/60 group">
                            <img src={p} alt="" className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => removeBotPreviewPhoto(i)}
                              className="absolute top-1 right-1 rounded-full bg-black/70 p-0.5 opacity-0 group-hover:opacity-100"
                              aria-label="Remove photo"
                            >
                              <X className="h-3 w-3 text-white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={saveBotPreview}
                    disabled={botSaving || !(botPreview.extracted.name || '').trim()}
                    className="w-full bg-gradient-to-r from-fuchsia-500 to-indigo-500 hover:opacity-90 text-white font-bold"
                    data-testid="bot-save-btn"
                  >
                    {botSaving
                      ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating & mirroring photos…</>
                      : <><Save className="h-4 w-4 mr-2" /> Create Venue</>}
                  </Button>
                </div>
              )}

              {!botPreview && !botScraping && (
                <p className="text-[10px] text-muted-foreground px-1">
                  💡 Can’t find the venue on their own site? Switch to the <b>Google Places</b> tab.
                </p>
              )}
            </TabsContent>

            {/* --- Google Places tab (fallback) --- */}
            <TabsContent value="google" className="space-y-3 pt-3">
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2">
                <Label className="text-amber-200 text-xs font-semibold flex items-center gap-2">
                  <Search className="h-3.5 w-3.5" /> Search Google Places
                </Label>
                <p className="text-[10px] text-muted-foreground">
                  Uses our Google Places quota. Photos get downloaded into our CDN and a 4-sentence editorial description is generated. Marked verified instantly.
                </p>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={importQuery}
                  onChange={(e) => setImportQuery(e.target.value)}
                  placeholder='e.g. "Dairy Block Denver" or "Frasca Boulder"'
                  className="pl-10 h-10 bg-muted border-border"
                  data-testid="import-places-search"
                />
                {importLoading && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>
              <div className="max-h-80 overflow-y-auto rounded-lg border border-border/60 divide-y divide-border/40">
                {importSuggestions.length === 0 && importQuery.trim().length >= 2 && !importLoading && (
                  <p className="text-xs text-muted-foreground p-4 text-center">
                    No matches yet — try adding the city name to narrow it down.
                  </p>
                )}
                {importSuggestions.map((p) => (
                  <button
                    key={p.place_id}
                    type="button"
                    onClick={() => importFromPlaces(p)}
                    disabled={importing}
                    className="w-full text-left p-3 hover:bg-muted/60 flex gap-3 items-start disabled:opacity-50 transition-colors"
                    data-testid={`import-suggestion-${p.place_id}`}
                  >
                    <MapPin className="h-4 w-4 mt-0.5 text-indigo-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{p.name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{p.formatted_address}</div>
                      <div className="flex items-center gap-2 mt-1 text-[10px]">
                        {p.rating && (
                          <span className="text-amber-400 font-semibold">★ {p.rating?.toFixed(1)}</span>
                        )}
                        {p.user_ratings_total ? (
                          <span className="text-muted-foreground">{p.user_ratings_total} reviews</span>
                        ) : null}
                        {(p.types || []).slice(0, 2).map((t) => (
                          <span key={t} className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            {String(t).replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                    <Plus className="h-4 w-4 text-fuchsia-400 flex-shrink-0 mt-0.5" />
                  </button>
                ))}
                {importing && (
                  <div className="p-3 flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Downloading photos &amp; generating editorial copy…
                  </div>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                <ExternalLink className="h-2.5 w-2.5" />
                Powered by Google Places. Photos saved to our CDN — no hot-linking.
              </p>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setImportOpen(false);
                setImportQuery('');
                setBotUrl('');
                setBotPreview(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual create Dialog — escape hatch when Places is unavailable */}
      <Dialog open={manualOpen} onOpenChange={(open) => { if (!open) setManualOpen(false); }}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="manual-create-modal">
          <DialogHeader>
            <DialogTitle className="text-foreground text-lg flex items-center gap-2">
              <Pencil className="h-5 w-5 text-emerald-400" /> Add Venue Manually
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              For venues Google Places can&apos;t find, or when you want to skip API costs. We&apos;ll still run a free background lookup — if Google knows the venue, we&apos;ll auto-attach photos + address.
            </p>
          </DialogHeader>

          {/* Free scrape row — paste a URL, we harvest name/description/photos from JSON-LD + OG tags */}
          <div className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-3 space-y-2" data-testid="scrape-url-block">
            <Label className="text-fuchsia-200 text-xs font-semibold flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> Auto-fill from a URL (free, no API)
            </Label>
            <div className="flex gap-2">
              <Input
                value={scrapeUrl}
                onChange={(e) => setScrapeUrl(e.target.value)}
                placeholder="https://greatdivide.com"
                className="flex-1"
                data-testid="scrape-url-input"
              />
              <Button
                onClick={scrapeVenueFromUrl}
                disabled={scraping || !scrapeUrl.trim()}
                variant="outline"
                className="border-fuchsia-500/50 text-fuchsia-200 hover:bg-fuchsia-500/10 shrink-0"
                data-testid="scrape-url-btn"
              >
                {scraping ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Reading…</> : <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Fetch</>}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Paste the venue&apos;s own website. We parse their public structured data (name, description, address, phone, hero image) — same info Google reads. Free, no keys, no rate limits.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 py-2">
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Venue name *</Label>
              <Input value={manualForm.name} onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })} placeholder="e.g. Great Divide Brewing Co." data-testid="manual-name" />
            </div>
            <div className="space-y-1.5">
              <Label>City *</Label>
              <Input value={manualForm.city} onChange={(e) => setManualForm({ ...manualForm, city: e.target.value })} data-testid="manual-city" />
            </div>
            <div className="space-y-1.5">
              <Label>State</Label>
              <Input value={manualForm.state} onChange={(e) => setManualForm({ ...manualForm, state: e.target.value })} placeholder="CO" data-testid="manual-state" />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Address</Label>
              <Input value={manualForm.address} onChange={(e) => setManualForm({ ...manualForm, address: e.target.value })} placeholder="2201 Arapahoe St" data-testid="manual-address" />
            </div>
            <div className="space-y-1.5">
              <Label>ZIP</Label>
              <Input value={manualForm.zip_code} onChange={(e) => setManualForm({ ...manualForm, zip_code: e.target.value })} data-testid="manual-zip" />
            </div>
            <div className="space-y-1.5">
              <Label>Neighborhood</Label>
              <Input value={manualForm.neighborhood} onChange={(e) => setManualForm({ ...manualForm, neighborhood: e.target.value })} placeholder="RiNo" data-testid="manual-neighborhood" />
            </div>
            <div className="space-y-1.5">
              <Label>Venue type</Label>
              <select
                value={manualForm.venue_type}
                onChange={(e) => setManualForm({ ...manualForm, venue_type: e.target.value })}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="manual-venue-type"
              >
                {['event_space','brewery','winery','wine_bar','cocktail_bar','bar','restaurant','cafe','nightclub','hotel','venue'].map((t) => (
                  <option key={t} value={t}>{t.replace(/_/g,' ')}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Price band</Label>
              <select
                value={manualForm.price_band}
                onChange={(e) => setManualForm({ ...manualForm, price_band: e.target.value })}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                data-testid="manual-price-band"
              >
                <option value="">— none —</option>
                <option value="$">$ — Budget</option>
                <option value="$$">$$ — Moderate</option>
                <option value="$$$">$$$ — Upscale</option>
                <option value="$$$$">$$$$ — Fine dining</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Capacity min</Label>
              <Input type="number" value={manualForm.capacity_min} onChange={(e) => setManualForm({ ...manualForm, capacity_min: e.target.value })} data-testid="manual-capacity-min" />
            </div>
            <div className="space-y-1.5">
              <Label>Capacity max</Label>
              <Input type="number" value={manualForm.capacity_max} onChange={(e) => setManualForm({ ...manualForm, capacity_max: e.target.value })} data-testid="manual-capacity-max" />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={manualForm.phone} onChange={(e) => setManualForm({ ...manualForm, phone: e.target.value })} data-testid="manual-phone" />
            </div>
            <div className="space-y-1.5">
              <Label>Website</Label>
              <Input value={manualForm.website} onChange={(e) => setManualForm({ ...manualForm, website: e.target.value })} placeholder="https://…" data-testid="manual-website" />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Description</Label>
              <Textarea rows={3} value={manualForm.description} onChange={(e) => setManualForm({ ...manualForm, description: e.target.value })} placeholder="One-paragraph editorial-voice description — vibe, what they're known for, when to go." data-testid="manual-description" />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="flex items-center justify-between">
                <span>Photos {(manualForm.photos || []).length > 0 && <span className="text-muted-foreground font-normal">({manualForm.photos.length})</span>}</span>
                <span className="text-[10px] text-muted-foreground font-normal">First becomes the cover</span>
              </Label>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                className="hidden"
                onChange={(e) => uploadVenuePhoto(e.target.files)}
                data-testid="manual-photo-file-input"
              />
              <div
                onClick={() => photoInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); uploadVenuePhoto(e.dataTransfer.files); }}
                className="border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-fuchsia-500/50 hover:bg-fuchsia-500/5 transition-colors"
                data-testid="manual-photo-dropzone"
              >
                {uploadingPhoto ? (
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-3">
                    <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
                  </div>
                ) : (
                  <>
                    <ImageIcon className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-foreground">Click to upload or drag & drop</p>
                    <p className="text-[10px] text-muted-foreground mt-1">JPG, PNG, WebP, or GIF · Max 10 MB each · Multiple allowed</p>
                  </>
                )}
              </div>
              {(manualForm.photos || []).length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-2" data-testid="manual-photo-thumbs">
                  {manualForm.photos.map((src, idx) => (
                    <div key={`${src}-${idx}`} className="relative group aspect-video rounded-md overflow-hidden border border-border">
                      <img src={src} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                      {idx === 0 && (
                        <span className="absolute top-1 left-1 text-[9px] font-bold bg-amber-500 text-black px-1.5 py-0.5 rounded uppercase tracking-wide">Cover</span>
                      )}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeVenuePhoto(idx); }}
                        className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        data-testid={`manual-photo-remove-${idx}`}
                        aria-label="Remove photo"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Tags / vibe (comma-separated)</Label>
              <Input value={manualForm.tags_csv} onChange={(e) => setManualForm({ ...manualForm, tags_csv: e.target.value })} placeholder="brewery, craft beer, patio" data-testid="manual-tags" />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Amenities (comma-separated)</Label>
              <Input value={manualForm.amenities_csv} onChange={(e) => setManualForm({ ...manualForm, amenities_csv: e.target.value })} placeholder="Outdoor Seating, Dog Friendly, Live Music" data-testid="manual-amenities" />
            </div>
            <label className="sm:col-span-2 flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={manualForm.auto_enrich}
                onChange={(e) => setManualForm({ ...manualForm, auto_enrich: e.target.checked })}
                data-testid="manual-auto-enrich"
              />
              Run background Google Places lookup after create (free-ride: attaches photos + lat/lng if Google finds it)
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setManualOpen(false)} disabled={manualSaving}>
              Cancel
            </Button>
            <Button
              onClick={saveManualVenue}
              disabled={manualSaving || !manualForm.name?.trim() || !manualForm.city?.trim()}
              className="bg-emerald-500 hover:bg-emerald-400 text-white border-0 font-bold"
              data-testid="manual-save"
            >
              {manualSaving ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Creating…</> : <><Save className="h-3.5 w-3.5 mr-1.5" /> Create venue</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog — URLs or CSV */}
      <Dialog open={bulkOpen} onOpenChange={(open) => { if (!open) { setBulkOpen(false); setBulkResult(null); } }}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-import-modal">
          <DialogHeader>
            <DialogTitle className="text-foreground text-lg flex items-center gap-2">
              <Upload className="h-5 w-5 text-fuchsia-400" /> Bulk Import Venues
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Import many venues in one shot — either paste a list of venue URLs and let <b className="text-fuchsia-300">EventismBot</b> scrape each one (free), or upload a CSV with every field including up to 5 photos, tags, and lat/lng.
            </p>
          </DialogHeader>

          {/* Mode tabs */}
          <div className="flex gap-2 border-b border-border mb-4" data-testid="bulk-mode-tabs">
            <button
              type="button"
              onClick={() => setBulkMode('urls')}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${bulkMode === 'urls' ? 'border-fuchsia-500 text-fuchsia-200' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid="bulk-mode-urls"
            >
              <Bot className="h-3.5 w-3.5 inline mr-1.5 text-fuchsia-400" />
              From URLs
              <span className="ml-1.5 text-[9px] text-emerald-300 font-bold align-middle">EVENTISMBOT</span>
            </button>
            <button
              type="button"
              onClick={() => setBulkMode('csv')}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${bulkMode === 'csv' ? 'border-emerald-500 text-emerald-200' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid="bulk-mode-csv"
            >
              <FileText className="h-3.5 w-3.5 inline mr-1.5" /> From CSV
            </button>
          </div>

          {bulkMode === 'urls' ? (
            <div className="space-y-3" data-testid="bulk-urls-panel">
              <div className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/5 p-3">
                <div className="flex items-start gap-2">
                  <Bot className="h-4 w-4 text-fuchsia-300 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-muted-foreground">
                    <p className="text-fuchsia-200 font-semibold mb-0.5 flex items-center gap-2">
                      Powered by EventismBot
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-bold">FREE</span>
                    </p>
                    Paste up to 50 venue websites. EventismBot reads each site&apos;s JSON-LD + Open Graph tags and creates a fully-populated venue row (name, address, photos, description, phone, lat/lng) per URL — no Google Places calls, no API keys, no rate limits.
                  </div>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Venue websites — one per line (max 50)</Label>
                <Textarea
                  rows={8}
                  value={bulkUrls}
                  onChange={(e) => setBulkUrls(e.target.value)}
                  placeholder={"https://greatdivide.com\nhttps://linger.com/denver\nhttps://williamsandgraham.com"}
                  className="font-mono text-xs"
                  data-testid="bulk-urls-textarea"
                />
                <p className="text-[10px] text-muted-foreground">
                  Skips duplicates by (city, name). Rows that fail to scrape are surfaced in the results below — you can retry them individually via the row-level Rehydrate button on the venues list.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="bulk-csv-panel">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label>CSV text — first row is headers</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={downloadCsvTemplate} data-testid="bulk-csv-template">
                    <Download className="h-3 w-3 mr-1" /> Download template
                  </Button>
                  <input
                    ref={bulkCsvFileRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={(e) => onBulkCsvFile(e.target.files?.[0])}
                    data-testid="bulk-csv-file-input"
                  />
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => bulkCsvFileRef.current?.click()} data-testid="bulk-csv-file-btn">
                    <Upload className="h-3 w-3 mr-1" /> Upload .csv
                  </Button>
                </div>
              </div>
              <Textarea
                rows={10}
                value={bulkCsv}
                onChange={(e) => setBulkCsv(e.target.value)}
                placeholder="name,city,state,address,venue_type,tags,photo1,photo2,lat,lng&#10;Great Divide Brewing Co.,Denver,CO,2201 Arapahoe St,brewery,&quot;craft beer,patio&quot;,https://…,https://…,39.7539,-105.0002"
                className="font-mono text-[11px] leading-relaxed"
                data-testid="bulk-csv-textarea"
              />
              <details className="text-[11px] text-muted-foreground">
                <summary className="cursor-pointer hover:text-foreground">Supported columns (all optional except <b>name</b>)</summary>
                <div className="mt-2 pl-3 space-y-1 leading-relaxed">
                  <p><b>Required:</b> <code>name</code></p>
                  <p><b>Location:</b> <code>city</code>, <code>state</code>, <code>address</code>, <code>zip_code</code> (or <code>zip</code>), <code>neighborhood</code>, <code>lat</code>, <code>lng</code></p>
                  <p><b>Contact:</b> <code>phone</code>, <code>website</code></p>
                  <p><b>Details:</b> <code>venue_type</code>, <code>description</code>, <code>capacity_min</code>, <code>capacity_max</code>, <code>price_band</code></p>
                  <p><b>Lists (comma-in-cell):</b> <code>tags</code>, <code>amenities</code></p>
                  <p><b>Photos:</b> <code>photo1</code>, <code>photo2</code>, <code>photo3</code>, <code>photo4</code>, <code>photo5</code>, or <code>photos</code> (comma-separated URLs)</p>
                </div>
              </details>
            </div>
          )}

          {/* Shared defaults */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="space-y-1.5">
              <Label>Default city (used when row is blank)</Label>
              <Input value={bulkCityDefault} onChange={(e) => setBulkCityDefault(e.target.value)} data-testid="bulk-city-default" />
            </div>
            <div className="space-y-1.5">
              <Label>Default state</Label>
              <Input value={bulkStateDefault} onChange={(e) => setBulkStateDefault(e.target.value)} data-testid="bulk-state-default" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground mt-3">
            <input
              type="checkbox"
              checked={bulkAutoEnrich}
              onChange={(e) => setBulkAutoEnrich(e.target.checked)}
              data-testid="bulk-auto-enrich"
            />
            Run background Google Places lookup on each new venue (free-ride: attaches photos + lat/lng when Google knows the venue)
          </label>

          {/* Result panel */}
          {bulkResult && (
            <div className="mt-4 rounded-lg border border-border bg-background/50 p-3 space-y-2" data-testid="bulk-result">
              <div className="flex gap-3 text-sm">
                <span className="text-emerald-400 font-semibold">
                  ✓ {bulkResult.summary?.created || 0} created
                </span>
                {bulkResult.summary?.skipped > 0 && (
                  <span className="text-amber-300 font-semibold">
                    ↷ {bulkResult.summary.skipped} skipped (duplicates)
                  </span>
                )}
                {bulkResult.summary?.errors > 0 && (
                  <span className="text-rose-400 font-semibold">
                    ✗ {bulkResult.summary.errors} error(s)
                  </span>
                )}
              </div>
              {bulkResult.errors?.length > 0 && (
                <div className="max-h-32 overflow-y-auto text-[11px] text-rose-300/90 font-mono">
                  {bulkResult.errors.map((e, i) => (
                    <div key={i} className="truncate">
                      {e.url ? `${e.url}` : e.row ? `row ${e.row}` : ''}: {e.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => { setBulkOpen(false); setBulkResult(null); }} disabled={bulkRunning}>
              Close
            </Button>
            <Button
              onClick={runBulkImport}
              disabled={bulkRunning || (bulkMode === 'urls' ? !bulkUrls.trim() : !bulkCsv.trim())}
              className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white border-0 font-bold"
              data-testid="bulk-run-btn"
            >
              {bulkRunning ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Importing…</> : <><Upload className="h-3.5 w-3.5 mr-1.5" /> Import</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editVenue} onOpenChange={(open) => { if (!open) setEditVenue(null); }}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Pencil className="h-5 w-5 text-indigo-400" /> Edit Venue
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Venue Name</Label>
              <Input value={editForm.name || ''} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} data-testid="edit-venue-name" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={editForm.description || ''} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} className="min-h-[80px]" data-testid="edit-venue-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Status</Label>
                <select value={editForm.status || 'active'} onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))} className="w-full h-10 rounded-md border border-border bg-muted text-foreground px-3 text-sm" data-testid="edit-venue-status">
                  <option value="active">Active</option>
                  <option value="pending_review">Pending</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Venue Type</Label>
                <Input value={editForm.venue_type || ''} onChange={e => setEditForm(p => ({ ...p, venue_type: e.target.value }))} placeholder="e.g. Ballroom, Rooftop" data-testid="edit-venue-type" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={editForm.phone || ''} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} data-testid="edit-venue-phone" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={editForm.email || ''} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} data-testid="edit-venue-email" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Website</Label>
              <Input value={editForm.website || ''} onChange={e => setEditForm(p => ({ ...p, website: e.target.value }))} placeholder="https://" data-testid="edit-venue-website" />
            </div>
            <div className="space-y-2">
              <Label>Max Capacity</Label>
              <Input type="number" value={editForm.max_capacity || ''} onChange={e => setEditForm(p => ({ ...p, max_capacity: e.target.value }))} data-testid="edit-venue-capacity" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Street</Label>
                <Input value={editForm.street || ''} onChange={e => setEditForm(p => ({ ...p, street: e.target.value }))} data-testid="edit-venue-street" />
              </div>
              <div className="space-y-2">
                <Label>City</Label>
                <Input value={editForm.city || ''} onChange={e => setEditForm(p => ({ ...p, city: e.target.value }))} data-testid="edit-venue-city" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>State</Label>
                <Input value={editForm.state || ''} onChange={e => setEditForm(p => ({ ...p, state: e.target.value }))} data-testid="edit-venue-state" />
              </div>
              <div className="space-y-2">
                <Label>ZIP</Label>
                <Input value={editForm.zip || ''} onChange={e => setEditForm(p => ({ ...p, zip: e.target.value }))} data-testid="edit-venue-zip" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditVenue(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-indigo-500 hover:bg-indigo-600" data-testid="save-venue-edit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-400" /> Delete Venue
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            Are you sure you want to permanently delete <strong className="text-foreground">{deleteConfirm?.name}</strong>? This will also remove all associated bookings. This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={() => deleteVenue(deleteConfirm.venue_id)} data-testid="confirm-delete-venue">
              <Trash2 className="h-4 w-4 mr-1" /> Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
